import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { productAPI } from '../services/api';
import toast from 'react-hot-toast';

const ProductImage = ({ image, name }) => {
  const isImage = image && (
    image.startsWith('http') ||
    image.startsWith('data:image') ||
    image.startsWith('/')
  );
  const isEmoji = image && image.length < 10 && !isImage;

  if (isImage) {
    return (
      <div className="h-48 bg-gradient-to-br from-gtg-blue-50 to-gtg-cyan-50 flex items-center justify-center overflow-hidden">
        <img
          src={image}
          alt={name}
          className="w-full h-full object-contain p-4"
          onError={(e) => {
            e.target.parentNode.innerHTML = '<div class="w-full h-full flex items-center justify-center"><span style="font-size:3.5rem">📦</span></div>';
          }}
        />
      </div>
    );
  }

  return (
    <div className="h-48 bg-gradient-to-br from-gtg-blue-100 to-gtg-cyan-100 flex items-center justify-center">
      <span className="text-6xl">{isEmoji ? image : '📦'}</span>
    </div>
  );
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, [selectedCategory, searchTerm]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (searchTerm) params.search = searchTerm;
      const response = await productAPI.getAll(params);
      setProducts(response.data.data.products);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await productAPI.getCategories();
      setCategories(['all', ...response.data.data.categories]);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      <div className="gtg-gradient text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-5xl font-bold mb-4">
            Our Products
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-xl opacity-90">
            Enterprise-grade IT equipment and infrastructure solutions
          </motion.p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-12">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gtg-cyan-500 focus:border-transparent"
            />
          </div>
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button key={category} onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-lg font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-gtg-blue-500 to-gtg-cyan-500 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}>
                {category === 'all' ? 'All Products' : category}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="loading-spinner"></div>
          </div>
        ) : products.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <motion.div key={product._id} whileHover={{ y: -10 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all">
                <ProductImage image={product.image} name={product.name} />
                <div className="p-6">
                  <div className="text-sm text-gtg-cyan-600 font-semibold mb-2">{product.category}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{product.name}</h3>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  {product.specifications?.length > 0 && (
                    <div className="mb-4">
                      <ul className="space-y-1">
                        {product.specifications.slice(0, 3).map((spec, index) => (
                          <li key={index} className="text-sm text-gray-600 flex items-center">
                            <span className="w-1.5 h-1.5 bg-gtg-cyan-500 rounded-full mr-2"></span>
                            {spec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <Link to="/contact"
                    className="w-full block text-center px-6 py-3 bg-gradient-to-r from-gtg-blue-500 to-gtg-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all">
                    Request Information
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-gray-600 text-lg">No products found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;